import React from "react";
import { afField } from "@autodromo/contextual-fields";
import { formatGtagLabel } from "../../../utils/formatGtagLabel";
import { sanitizeSvg } from "../../../utils/sanitize";
import {
  clampSubmenuColumns,
  getLinkUrl,
  linkKey,
  resolveMenuItemMode,
  shouldOpenInNewTab,
  submenuIsWide,
} from "../logic";
import { STRINGS } from "../strings";
import { Chevron } from "./Icons";
import { SubmenuItems } from "./SubmenuItems";
import { SubmenuPanel } from "./SubmenuPanel";
import { MenuItem, MenuItemButton, MenuItemLink, MenuItems, Nav, SubmenuLink, SubmenuLinks } from "../HeaderV2.styles";
import {
  MenuItemMode,
  type HeaderV2Link,
  type MegaMenuData,
  type MenuAlign,
  type MenuSpacing,
  type SubmenuStyle,
} from "../types";

type DesktopMenuProps = {
  links: HeaderV2Link[];
  align?: MenuAlign;
  spacing?: MenuSpacing;
  submenuStyle?: SubmenuStyle;
  withLogo?: boolean;
  htmlId?: string;
  openId: string | null;
  megaMenuData?: MegaMenuData;
  panelIdFor: (linkId: string) => string;
  opensOnHover: boolean;
  onHoverOpen: (id: string) => void;
  onToggle: (id: string) => void;
};

export const DesktopMenu = ({
  links,
  align,
  spacing,
  submenuStyle,
  withLogo,
  htmlId,
  openId,
  megaMenuData,
  panelIdFor,
  opensOnHover,
  onHoverOpen,
  onToggle,
}: DesktopMenuProps) => (
  <Nav aria-label={STRINGS.mainNavigation} $align={align} $withLogo={withLogo} data-header-menu="">
    <MenuItems $spacing={spacing}>
      {links.map((link, index) => {
        const id = linkKey({ link, index });
        const mode = resolveMenuItemMode({ link, megaMenuData });
        const isOpen = openId === id;
        const gtag = {
          "data-gtag-action": "Clique Menu",
          "data-gtag-label": formatGtagLabel(htmlId, link.name),
          "data-gtag-category": "Cabeçalho",
        };

        if (mode === MenuItemMode.LINK) {
          return (
            <MenuItem key={id} data-af-field={afField("links", link.id)}>
              <MenuItemLink
                href={getLinkUrl({ item: link })}
                target={shouldOpenInNewTab({ item: link }) ? "_blank" : "_self"}
                data-af-field={afField("links", link.id, "name")}
                {...gtag}
              >
                {link.icon && (
                  <span className="hv2-menu-icon" aria-hidden="true" dangerouslySetInnerHTML={{ __html: sanitizeSvg(link.icon) }} />
                )}
                {link.name}
              </MenuItemLink>
            </MenuItem>
          );
        }

        const columns = clampSubmenuColumns({ value: link.submenuColumns });

        return (
          <MenuItem key={id} data-af-field={afField("links", link.id)}>
            <MenuItemButton
              type="button"
              aria-expanded={isOpen}
              aria-controls={mode === MenuItemMode.MEGAMENU ? panelIdFor(id) : undefined}
              data-menu-trigger={id}
              data-af-field={afField("links", link.id, "name")}
              onClick={() => onToggle(id)}
              onPointerEnter={
                opensOnHover
                  ? (event) => {
                      if (event.pointerType === "touch") return;
                      onHoverOpen(id);
                    }
                  : undefined
              }
              {...gtag}
            >
              {link.icon && (
                <span className="hv2-menu-icon" aria-hidden="true" dangerouslySetInnerHTML={{ __html: sanitizeSvg(link.icon) }} />
              )}
              {link.name}
              <Chevron className="hv2-chevron" />
            </MenuItemButton>

            {mode === MenuItemMode.SUBMENU && isOpen && (
              <SubmenuPanel label={link.name} wide={submenuIsWide({ link })} columns={columns} submenuStyle={submenuStyle}>
                <SubmenuLinks $columns={columns}>
                  <SubmenuItems link={link} htmlId={htmlId} LinkComponent={SubmenuLink} />
                </SubmenuLinks>
              </SubmenuPanel>
            )}
          </MenuItem>
        );
      })}
    </MenuItems>
  </Nav>
);
